import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Link, useNavigate } from "react-router-dom";
import { Sparkles, Zap, Shield, Users, ArrowRight, Check, Loader2 } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useState } from "react";
import type { Database } from "@/lib/supabase/types";
import { getPlanDescription, getPlanFeatures, formatPlanPrice } from "@/lib/utils/plans";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

type Plan = Database["public"]["Tables"]["planos"]["Row"];

const features = [{
  icon: Sparkles,
  title: "IA com Contexto Corporativo",
  description: "Acesso liberado por assinatura com assistente treinado no contexto interno da sua empresa."
}, {
  icon: Zap,
  title: "Economia Compartilhada",
  description: "Uma única chave API para toda a equipe. Compartilhe custos de IA entre todos os colaboradores e economize até 70% comparado a assinaturas individuais."
}, {
  icon: Shield,
  title: "Segurança e Privacidade",
  description: "Arquitetura de dados com isolamento completo, garantindo privacidade e proteção total das suas informações."
}, {
  icon: Users,
  title: "Painel de Gestão Completa",
  description: "Gerencie colaboradores, controle acessos e configure as regras de uso da IA por departamento."
}];

export default function Landing() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [loadingCheckout, setLoadingCheckout] = useState<number | null>(null);
  const [registerDialogOpen, setRegisterDialogOpen] = useState(false);
  const [registerForm, setRegisterForm] = useState({
    name: "",
    email: "",
    company: "",
    telefone: "",
    password: "",
    confirmPassword: "",
  });
  const [registerLoading, setRegisterLoading] = useState(false);

  // Fetch plans from database (exclui planos master-only)
  const { data: plans, isLoading } = useQuery({
    queryKey: ["plans"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("planos")
        .select("*")
        .eq("is_active", true)
        .eq("is_master_only", false) // Exclui planos exclusivos para master
        .order("ordem_exibicao", { ascending: true })
        .order("preco_mensal", { ascending: true });

      if (error) throw error;
      return data as Plan[];
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
  });

  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validações básicas
    if (!registerForm.email || !registerForm.password || !registerForm.name || !registerForm.company) {
      toast({
        title: "Erro",
        description: "Preencha todos os campos obrigatórios",
        variant: "destructive",
      });
      return;
    }

    if (registerForm.password !== registerForm.confirmPassword) {
      toast({
        title: "Erro",
        description: "As senhas não coincidem",
        variant: "destructive",
      });
      return;
    }

    if (registerForm.password.length < 6) {
      toast({
        title: "Erro",
        description: "A senha deve ter pelo menos 6 caracteres",
        variant: "destructive",
      });
      return;
    }

    setRegisterLoading(true);

    try {
      // Criar usuário
      const { data: authData, error: authError } = await supabase.auth.signUp({
        email: registerForm.email,
        password: registerForm.password,
        options: {
          data: {
            full_name: registerForm.name,
          },
        },
      });

      if (authError) throw authError;

      if (!authData.user) {
        throw new Error("Falha ao criar usuário");
      }

      // Obter sessão
      let session = authData.session;
      if (!session) {
        const { data: sessionData } = await supabase.auth.getSession();
        session = sessionData?.session || null;
      }

      // Criar tenant (empresa + perfil)
      // Não enviamos Authorization header se não tivermos sessão válida
      // A função create-tenant usará user_id do body para autenticação
      const { data: tenantData, error: tenantError } = await supabase.functions.invoke(
        "create-tenant",
        {
          body: {
            nome: registerForm.company,
            email: registerForm.email,
            telefone: registerForm.telefone || undefined,
            user_id: authData.user.id,
          },
        }
      );

      if (tenantError || !tenantData?.success) {
        throw new Error(tenantError?.message || "Falha ao criar empresa");
      }

      toast({
        title: "Sucesso!",
        description: "Conta criada com sucesso. Redirecionando...",
      });

      // Fechar modal
      setRegisterDialogOpen(false);

      // Redirecionar para dashboard (plano Free não precisa de checkout)
      const role = tenantData.perfil?.role || "user";
      if (role === "master") {
        navigate("/dashboard/master");
      } else if (role === "admin") {
        navigate("/dashboard/admin");
      } else {
        navigate("/dashboard/colaborador");
      }
    } catch (error: any) {
      console.error("Registration error:", error);
      toast({
        title: "Erro ao criar conta",
        description: error.message || "Ocorreu um erro inesperado",
        variant: "destructive",
      });
    } finally {
      setRegisterLoading(false);
    }
  };

  const handleCheckout = async (planId: number, planName: string) => {
    if (planName === "Free" || Number(plans?.find(p => p.id === planId)?.preco_mensal) === 0) {
      // Free plan - abrir modal de cadastro
      setRegisterDialogOpen(true);
      return;
    }

    setLoadingCheckout(planId);

    try {
      // Verificar se o usuário está logado
      const { data: { session } } = await supabase.auth.getSession();
      
      // Buscar o plano para verificar se tem stripe_price_id
      const plan = plans?.find((p) => p.id === planId);
      
      if (!plan) {
        throw new Error("Plano não encontrado");
      }

      if (!plan.stripe_price_id) {
        toast({
          title: "Plano não configurado",
          description: "Este plano ainda não foi sincronizado com o Stripe. Entre em contato com o suporte.",
          variant: "destructive",
        });
        setLoadingCheckout(null);
        return;
      }

      // Preparar o body da requisição
      const requestBody: {
        plano_id: number;
        empresa_id?: number;
        email?: string;
      } = {
        plano_id: planId,
      };

      // Se o usuário está logado, tentar obter o empresa_id
      if (session?.user) {
        const { data: perfil } = await supabase
          .from("perfis")
          .select("empresa_id")
          .eq("auth_user_id", session.user.id)
          .single();

        if (perfil?.empresa_id) {
          requestBody.empresa_id = perfil.empresa_id;
        } else {
          // Usuário logado mas sem empresa - usar email
          requestBody.email = session.user.email || undefined;
        }
      } else {
        // Usuário não logado - redirecionar para registro com o plano selecionado
        navigate(`/auth/register?plano=${planId}`);
        setLoadingCheckout(null);
        return;
      }

      // Criar sessão de checkout
      const { data, error } = await supabase.functions.invoke("create-checkout-session", {
        body: requestBody,
      });

      if (error) throw error;

      if (data?.url) {
        // Redirecionar para o checkout do Stripe
        window.location.href = data.url;
      } else {
        throw new Error("URL de checkout não retornada");
      }
    } catch (error: any) {
      console.error("Checkout error:", error);
      toast({
        title: "Erro ao iniciar checkout",
        description: error.message || "Falha ao iniciar o processo de pagamento. Tente novamente.",
        variant: "destructive",
      });
      setLoadingCheckout(null);
    }
  };

  // formatPrice, getPlanFeatures, and getPlanDescription are imported from utils/plans

  const isHighlighted = (plan: Plan) => {
    // Usar campo destacar do banco, ou fallback para preço R$199,90
    if (plan.destacar !== undefined && plan.destacar !== null) {
      return plan.destacar;
    }
    // Fallback: destacar o plano de R$199,90 como "Mais Vendido"
    const price = Number(plan.preco_mensal);
    return Math.abs(price - 199.90) < 0.01; // Tolerância para comparação de float
  };
  return <div className="min-h-screen">
      {/* Navigation */}
      <nav className="sticky top-0 z-50 border-b border-border bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
        <div className="container mx-auto flex h-16 items-center justify-between px-4">
          <h1 className="text-2xl font-bold bg-hero-gradient bg-clip-text text-transparent">
            Orbita Control IA
          </h1>
          <div className="flex items-center gap-4">
            <Link to="/auth/login">
              <Button variant="ghost">Login</Button>
            </Link>
            <Link to="/auth/register">
              <Button className="bg-primary text-primary-foreground hover:bg-primary/90 shadow-glow-primary">
                Começar Agora
              </Button>
            </Link>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative overflow-hidden py-20 md:py-32">
        <div className="absolute inset-0 bg-hero-gradient opacity-20" />
        <div className="container relative mx-auto px-4">
          <div className="mx-auto max-w-4xl text-center">
            
            <h2 className="mb-6 text-4xl font-bold tracking-tight md:text-6xl">
              Plataforma de IA{" "}
              <span className="bg-hero-gradient bg-clip-text text-transparent">
                Privada e Segura
              </span>
            </h2>
            <p className="mb-8 text-lg text-muted-foreground md:text-xl">Seu ChatGPT Privado com controle corporativo total. Uma única chave API compartilhada por toda a organização. Economize recursos e mantenha controle total sobre custos e segurança.</p>
            <div className="flex flex-col items-center justify-center gap-4 sm:flex-row">
              <Link to="/auth/register">
                <Button size="lg" className="bg-primary text-primary-foreground hover:bg-primary/90 shadow-glow-primary">
                  Comece Gratuitamente
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
              <Button size="lg" variant="outline" className="border-primary/20 hover:bg-primary/5">
                Ver Demonstração
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 border-t border-border">
        <div className="container mx-auto px-4">
          <div className="mb-12 text-center">
            <h3 className="mb-4 text-3xl font-bold">Recursos Poderosos</h3>
            <p className="text-lg text-muted-foreground">
              Tudo que você precisa para automatizar e escalar seu negócio
            </p>
          </div>
          <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-4">
            {features.map((feature, index) => <Card key={index} className="border-border bg-card hover:border-primary/50 transition-all">
                <CardHeader>
                  <div className="mb-2 h-12 w-12 rounded-lg bg-primary/10 flex items-center justify-center">
                    <feature.icon className="h-6 w-6 text-primary" />
                  </div>
                  <CardTitle>{feature.title}</CardTitle>
                </CardHeader>
                <CardContent>
                  <CardDescription className="text-muted-foreground">
                    {feature.description}
                  </CardDescription>
                </CardContent>
              </Card>)}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 border-t border-border bg-card/30">
        <div className="container mx-auto px-4">
          <div className="mx-auto max-w-4xl">
            <div className="text-center mb-12">
              <h3 className="mb-4 text-3xl font-bold">Economia Inteligente</h3>
              <p className="text-lg text-muted-foreground">
                Compartilhe uma única chave API com toda a equipe e reduza custos drasticamente
              </p>
            </div>
            <div className="grid gap-6 md:grid-cols-2">
              <Card className="border-border bg-card p-6">
                <div className="mb-4">
                  <div className="text-4xl font-bold text-primary">70%</div>
                  <div className="text-sm text-muted-foreground">Economia em relação a múltiplas assinaturas</div>
                </div>
                <p className="text-muted-foreground">
                  Em vez de cada colaborador pagar uma assinatura individual de IA, toda a equipe usa uma única chave API compartilhada.
                </p>
              </Card>
              <Card className="border-border bg-card p-6">
                <div className="mb-4">
                  <div className="text-4xl font-bold text-primary">1</div>
                  <div className="text-sm text-muted-foreground">Chave API para toda organização</div>
                </div>
                <p className="text-muted-foreground">
                  Centralize o custo de IA em uma única conta OpenAI ou Claude. Visibilidade total dos gastos e controle unificado.
                </p>
              </Card>
              <Card className="border-border bg-card p-6">
                <div className="mb-4">
                  <div className="text-4xl font-bold text-primary">100%</div>
                  <div className="text-sm text-muted-foreground">Controle sobre uso e gastos</div>
                </div>
                <p className="text-muted-foreground">
                  Configure limites por usuário, departamento ou projeto. Acompanhe em tempo real quanto cada área está consumindo.
                </p>
              </Card>
              <Card className="border-border bg-card p-6">
                <div className="mb-4">
                  <div className="text-4xl font-bold text-primary">∞</div>
                  <div className="text-sm text-muted-foreground">Uso ilimitado conforme sua API</div>
                </div>
                <p className="text-muted-foreground">
                  Não há limite artificial de uso. Seus colaboradores usam o quanto precisarem dentro dos limites da sua própria chave API.
                </p>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="py-20 border-t border-border">
        <div className="container mx-auto px-4">
          <div className="mb-12 text-center">
            <h3 className="mb-4 text-3xl font-bold">Planos Flexíveis</h3>
            <p className="text-lg text-muted-foreground">
              Escolha o seu plano de gestão e segurança. Você controla os custos de IA usando sua própria chave de API.
            </p>
          </div>
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
          ) : (
            <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-4">
              {plans?.map((plan) => {
                const highlighted = isHighlighted(plan);
                return (
                  <Card
                    key={plan.id}
                    className={`relative border-border ${highlighted ? "border-primary shadow-glow-primary scale-105" : "hover:border-primary/50"} transition-all`}
                  >
                    {highlighted && (
                      <div className="absolute -top-4 left-1/2 -translate-x-1/2">
                        <Badge className="bg-primary text-primary-foreground">
                          {plan.badge_text || "Mais Vendido"}
                        </Badge>
                      </div>
                    )}
                    <CardHeader>
                      <CardTitle>{plan.nome}</CardTitle>
                      <CardDescription>
                        {getPlanDescription(plan)}
                      </CardDescription>
                      <div className="mt-4">
                        <span className="text-4xl font-bold">{formatPlanPrice(Number(plan.preco_mensal))}</span>
                        {plan.preco_mensal > 0 && <span className="text-muted-foreground">/mês</span>}
                      </div>
                    </CardHeader>
                    <CardContent>
                      <ul className="space-y-3">
                        {getPlanFeatures(plan).map((feature, fIndex) => (
                          <li key={fIndex} className="flex items-center gap-2">
                            <Check className="h-4 w-4 text-primary" />
                            <span className="text-sm">{feature}</span>
                          </li>
                        ))}
                      </ul>
                    </CardContent>
                    <CardFooter>
                      <Button
                        className={`w-full ${highlighted ? "bg-primary text-primary-foreground hover:bg-primary/90" : "bg-muted hover:bg-muted/80"}`}
                        onClick={() => handleCheckout(plan.id, plan.nome)}
                        disabled={loadingCheckout === plan.id}
                      >
                        {loadingCheckout === plan.id ? (
                          <>
                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                            Processando...
                          </>
                        ) : (
                          "Começar Agora"
                        )}
                      </Button>
                    </CardFooter>
                  </Card>
                );
              })}
            </div>
          )}
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-border py-12">
        <div className="container mx-auto px-4">
          <div className="text-center">
            <h4 className="mb-2 text-xl font-bold bg-hero-gradient bg-clip-text text-transparent">
              Orbita Control IA
            </h4>
            <p className="text-sm text-muted-foreground">
              © 2026 Orbita Control IA. Todos os direitos reservados.
            </p>
          </div>
        </div>
      </footer>

      {/* Register Dialog for Free Plan */}
      <Dialog open={registerDialogOpen} onOpenChange={setRegisterDialogOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Criar Conta Grátis</DialogTitle>
            <DialogDescription>
              Preencha os dados para começar gratuitamente
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleRegister} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="register-name">Nome Completo</Label>
              <Input
                id="register-name"
                type="text"
                placeholder="João Silva"
                value={registerForm.name}
                onChange={(e) => setRegisterForm({...registerForm, name: e.target.value})}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="register-company">Empresa</Label>
              <Input
                id="register-company"
                type="text"
                placeholder="Minha Empresa Ltda"
                value={registerForm.company}
                onChange={(e) => setRegisterForm({...registerForm, company: e.target.value})}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="register-email">Email</Label>
              <Input
                id="register-email"
                type="email"
                placeholder="seu@email.com"
                value={registerForm.email}
                onChange={(e) => setRegisterForm({...registerForm, email: e.target.value})}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="register-telefone">Telefone (Opcional)</Label>
              <Input
                id="register-telefone"
                type="tel"
                placeholder="(11) 99999-9999"
                value={registerForm.telefone}
                onChange={(e) => setRegisterForm({...registerForm, telefone: e.target.value})}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="register-password">Senha</Label>
              <Input
                id="register-password"
                type="password"
                placeholder="••••••••"
                value={registerForm.password}
                onChange={(e) => setRegisterForm({...registerForm, password: e.target.value})}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="register-confirm-password">Confirmar Senha</Label>
              <Input
                id="register-confirm-password"
                type="password"
                placeholder="••••••••"
                value={registerForm.confirmPassword}
                onChange={(e) => setRegisterForm({...registerForm, confirmPassword: e.target.value})}
                required
              />
            </div>
            <div className="flex gap-2 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => setRegisterDialogOpen(false)}
                className="flex-1"
              >
                Cancelar
              </Button>
              <Button
                type="submit"
                className="flex-1 bg-primary text-primary-foreground hover:bg-primary/90"
                disabled={registerLoading}
              >
                {registerLoading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Criando...
                  </>
                ) : (
                  "Criar Conta"
                )}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>;
}